import { ReactNode, useMemo } from 'react'

import {
  createTheme,
  CssBaseline,
  experimental_extendTheme,
  GlobalStyles,
  StyledEngineProvider,
  Theme,
  ThemeOptions,
  TypographyVariantsOptions,
} from '@mui/material'
import { Experimental_CssVarsProvider as CssVarsProvider } from '@mui/material/styles'

import useConfig from 'hooks/useConfig'
import { BRAND_ACCENT, BRAND_ACCENT_HOVER, BRAND_ACCENT_MAIN_CHANNEL } from './brandColors'
import componentsOverride from './overrides'
import Palette from './palette'
import CustomShadows from './shadows'
import Typography from './typography'

import { CustomShadowProps } from 'types'

type ThemeCustomizationProps = {
  children: ReactNode
}

// ==============================|| DEFAULT THEME - MAIN  ||============================== //

export default function ThemeCustomization({
  children,
}: ThemeCustomizationProps) {
  const { themeDirection, mode, presetColor, fontFamily } = useConfig()

  const theme: Theme = useMemo<Theme>(
    () => Palette(mode, presetColor),
    [mode, presetColor]
  )

  const themeTypography: TypographyVariantsOptions =
    useMemo<TypographyVariantsOptions>(
      () => Typography(mode, fontFamily, theme),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [mode, fontFamily]
    )
  const themeCustomShadows: CustomShadowProps = useMemo<CustomShadowProps>(
    () => CustomShadows(theme),
    [theme]
  )

  const themeOptions: ThemeOptions = useMemo(
    () => ({
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1440,
          xxl: 1720,
        },
      },
      direction: themeDirection,
      mixins: {
        toolbar: {
          minHeight: 60,
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
      palette: theme.palette,
      customShadows: themeCustomShadows,
      typography: themeTypography,
    }),
    [themeDirection, theme, themeTypography, themeCustomShadows]
  )

  const themes: Theme = createTheme(themeOptions)
  /** Apply component overrides before extendTheme so CSS vars + theme share one palette */
  themes.components = componentsOverride(themes)
  const themeVars = experimental_extendTheme(themes)

  return (
    <StyledEngineProvider injectFirst>
      <CssVarsProvider theme={themeVars}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            ':root': {
              '--mui-palette-secondary-main': BRAND_ACCENT,
              '--mui-palette-secondary-light': BRAND_ACCENT_HOVER,
              '--mui-palette-secondary-mainChannel': BRAND_ACCENT_MAIN_CHANNEL,
              '--mui-palette-info-main': BRAND_ACCENT,
            },
          }}
        />
        {children}
      </CssVarsProvider>
    </StyledEngineProvider>
  )
}
